import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Badge, Code, type Tone } from './Badge';
import { money, qty as fmtQty, humanize } from '@/lib/format';
import type { TransactionLineItem, TransactionTender } from '@/lib/api/types';
import { changeTotal, tenderedTotal } from '@/lib/api/mappers';

/**
 * Receipt rendering, shared by the transactions inspector and the full receipt
 * screen so a receipt never reads two different ways in two places.
 *
 * Everything here assumes it has already been through `@/lib/api/mappers` —
 * `GET /transactions/:id` hands back raw DB rows, and rendering those directly
 * silently produces a blank receipt for a transaction that is perfectly fine.
 */

/** One lookup for transaction status → tone, per the design rule. */
export const TXN_STATUS_TONE: Record<string, Tone> = {
  completed: 'positive',
  refund: 'warn',
  voided: 'negative',
  suspended: 'info',
  training: 'neutral',
};

export function TxnStatusBadge({ status }: { status: string | null | undefined }) {
  const value = status ?? 'unknown';
  return (
    <Badge tone={TXN_STATUS_TONE[value] ?? 'neutral'} dot>
      {humanize(value)}
    </Badge>
  );
}

/** A label/value row inside a totals block. Money is always right-aligned mono. */
export function AmountRow({
  label,
  value,
  tone,
  strong = false,
  hint,
}: {
  label: ReactNode;
  value: number | null | undefined;
  tone?: 'positive' | 'negative' | 'muted';
  strong?: boolean;
  hint?: ReactNode;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-0.5">
      <span
        className={cn(
          'min-w-0 truncate text-xs',
          strong ? 'text-ink font-medium' : 'text-ink-muted'
        )}
      >
        {label}
        {hint ? <span className="text-2xs text-ink-faint ml-1.5">{hint}</span> : null}
      </span>
      <span
        className={cn(
          'shrink-0 font-mono tabular-nums',
          strong ? 'text-ink text-sm font-medium' : 'text-ink text-xs',
          tone === 'positive' && 'text-positive',
          tone === 'negative' && 'text-negative',
          tone === 'muted' && 'text-ink-faint'
        )}
      >
        {money(value)}
      </span>
    </div>
  );
}

/** The line items. Return lines are marked — they are the ones that move stock. */
export function ReceiptLines({ items }: { items: TransactionLineItem[] }) {
  if (items.length === 0) {
    return (
      <p className="text-ink-faint py-2 text-xs">
        This transaction has no line items recorded.
      </p>
    );
  }

  return (
    <ul className="divide-line-soft divide-y">
      {items.map((item, index) => (
        <li
          key={item.id ?? `${item.description}-${index}`}
          className={cn('flex items-start gap-3 py-1.5', item.isVoided && 'opacity-50')}
        >
          <span className="text-ink-muted w-12 shrink-0 pt-px text-right font-mono text-xs tabular-nums">
            {fmtQty(item.quantity)}
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-1.5">
              <span
                className={cn(
                  'text-ink truncate text-xs',
                  item.isVoided && 'line-through'
                )}
              >
                {item.description || 'Unnamed item'}
              </span>
              {item.lineType === 'return' ? <Badge tone="warn">Return</Badge> : null}
              {item.lineType === 'coupon' ? <Badge tone="info">Coupon</Badge> : null}
              {item.isVoided ? <Badge tone="negative">Voided</Badge> : null}
            </span>
            <span className="text-2xs text-ink-faint mt-0.5 flex flex-wrap items-center gap-2">
              <span className="font-mono tabular-nums">{money(item.unitPrice)} ea</span>
              {item.discount ? (
                <span className="text-positive font-mono tabular-nums">
                  −{money(item.discount)}
                </span>
              ) : null}
              {item.tax ? (
                <span className="font-mono tabular-nums">tax {money(item.tax)}</span>
              ) : null}
              {item.departmentName ? <span>{item.departmentName}</span> : null}
              {item.plu ? <Code>PLU {item.plu}</Code> : null}
              {!item.plu && item.barcode ? <Code copyable>{item.barcode}</Code> : null}
            </span>
          </span>
          <span className="text-ink shrink-0 pt-px font-mono text-xs tabular-nums">
            {money(item.total)}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Tenders, with the card/recovery metadata the register stashed in `meta`. */
export function ReceiptTenders({ tenders }: { tenders: TransactionTender[] }) {
  if (tenders.length === 0) {
    return (
      <p className="text-warn py-2 text-xs">
        No tender was recorded against this transaction.
      </p>
    );
  }

  return (
    <ul className="divide-line-soft divide-y">
      {tenders.map((tender, index) => (
        <li
          key={tender.id ?? `${tender.type}-${index}`}
          className="flex items-start gap-3 py-1.5"
        >
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-1.5">
              <span className="text-ink text-xs font-medium">
                {humanize(tender.type)}
              </span>
              {tender.last4 ? <Code>•••• {tender.last4}</Code> : null}
              {tender.recoverySource ? (
                <Badge tone="signal">{humanize(tender.recoverySource)}</Badge>
              ) : null}
            </span>
            <span className="text-2xs text-ink-faint mt-0.5 flex flex-wrap items-center gap-2">
              {tender.authCode ? <span>auth {tender.authCode}</span> : null}
              {tender.entryMethod ? <span>{humanize(tender.entryMethod)}</span> : null}
              {tender.reference ? <Code copyable>{tender.reference}</Code> : null}
              {tender.recoveryReason ? <span>“{tender.recoveryReason}”</span> : null}
            </span>
          </span>
          <span className="shrink-0 text-right">
            <span className="text-ink block font-mono text-xs tabular-nums">
              {money(tender.amount)}
            </span>
            {tender.change ? (
              <span className="text-2xs text-ink-faint block font-mono tabular-nums">
                change {money(tender.change)}
              </span>
            ) : null}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The money block. `tendered` and `change` are derived from the tenders rather
 * than trusted from a header field — the header only carries the sale total.
 */
export function ReceiptTotals({
  subtotal,
  discountTotal,
  taxTotal,
  total,
  tenders,
}: {
  subtotal?: number | null;
  discountTotal?: number | null;
  taxTotal?: number | null;
  total: number;
  tenders: TransactionTender[];
}) {
  const tendered = tenderedTotal(tenders);
  const change = changeTotal(tenders);
  const shortfall = total - (tendered - change);

  return (
    <div className="space-y-px">
      <AmountRow label="Subtotal" value={subtotal ?? 0} />
      {discountTotal ? (
        <AmountRow label="Discounts" value={-discountTotal} tone="positive" />
      ) : null}
      <AmountRow label="Tax" value={taxTotal ?? 0} />
      <div className="border-line my-1 border-t" />
      <AmountRow label="Total" value={total} strong />
      <AmountRow
        label="Tendered"
        value={tendered}
        hint={tenders.length > 1 ? `${tenders.length} tenders` : undefined}
      />
      {change ? <AmountRow label="Change" value={change} tone="muted" /> : null}
      {tenders.length > 0 && shortfall !== 0 ? (
        <p className="text-2xs text-warn mt-1">
          Tenders do not settle this receipt — {money(Math.abs(shortfall))}{' '}
          {shortfall > 0 ? 'short' : 'over'}.
        </p>
      ) : null}
    </div>
  );
}
